import React, { useState, useEffect, useRef } from "react";
import { components as ReactSelectComponents } from "react-select";
import LoadingIndicator from "../../common/loading_indicator.jsx";
import Select from "../../common/Select.jsx";
import { FaIcon } from "../../common/Icons.jsx";

const CaretDropdownIndicator = (props) => (
  <ReactSelectComponents.DropdownIndicator {...props}>
    <FaIcon type={props.selectProps.menuIsOpen ? "caret-down" : "caret-right"} />
  </ReactSelectComponents.DropdownIndicator>
);
import { update } from "../../../helpers/index.js";
import PdfButton from "../../common/pdf/ConfigurationCreator.jsx";
import { DEFAULT_DESIGN } from "../../common/pdf/DesignConfiguration.jsx";
import RichText from "../../common/RichText.jsx";

import {
  project_report_bulk_pdf_options_path,
  project_report_pdf_options_path,
  project_report_external_bulk_pdf_path,
  project_report_pdf_path,
  project_report_export_fill_phase_forms_as_pdf_path
} from '~/routes';

const PHASE_FORM_OPTIONS = ["form", "comments"];

const ReportPdfContentConfiguration = ({
  projectReportId,
  mayViewComments,
  mayViewAllComments,
  maySelectVersion,
  defaultVersion,
  onConfigChange
}) => {
  const formRef = useRef(0);
  const [data, setData] = useState({ loading: true });
  const [selectedVersion, setSelectedVersion] = useState({});
  const [selected, setSelected] = useState(
    Object.fromEntries(PHASE_FORM_OPTIONS.map((o) => [o, []]))
  );
  const [disClaimerChecked, setDisclaimerChecked] = useState(false);
  const [expandedPhases, setExpandedPhases] = useState({});
  const [historyOption, setHistoryOption] = useState('none');

  // Can also be called with an array of projectReportIds.
  const isBulkAction = Array.isArray(projectReportId);
  const optionAction = isBulkAction
    ? project_report_bulk_pdf_options_path({ project_report_ids: projectReportId.join('_') })
    : project_report_pdf_options_path({ project_report_id: projectReportId });


  useEffect(() => {
    jQuery.getJSON(optionAction,
      (data) => {
        setData({ ...data, loading: false });
      }
    );
  }, [projectReportId]);

  useEffect(() => {
    if (!data.loading) {
      const selected = {};
      // Use default version if given and exist, or latest otherwise
      data.phases.forEach(
        (p) =>
          (selected[p.id] = defaultVersion
            ? (p.version_options.slice(defaultVersion * -1)[0] || {}).value
            : p.version_options[0].value || p.version_options[0].value)
      );
      setSelectedVersion(selected);
      setExpandedPhases(Object.fromEntries(data.phases.map((p) => [p.id, true])));
    }
  }, [data.loading]);

  const onConfigUpdated = () => onConfigChange({content: $(formRef.current).serialize()}) 

  const onDisclaimerCheck = (e) => {
    onConfigChange();
    setDisclaimerChecked(e.target.value === "true");
  }

  useEffect(() => {
    onConfigUpdated();
  }, [selected, selectedVersion])

  const updatePhaseVersion = (id, version) => {
    setSelectedVersion(
      update(selectedVersion, {
        [id]: {
          $set: version,
        },
      })
    );
  };

  const { phases = [], loading } = data;

  const selectableFormIds = React.useMemo(() => {
    return {
      form: phases.flatMap((p) => p.forms.map((f) => f.link_id)),
      comments: phases.flatMap((p) =>
        p.forms
          .filter((f) => {
            const versionIdx = p.version_options.findIndex((o) => o.value == selectedVersion[p.id]);
            const isLatest = versionIdx == 0;
            return (
              (f.answers[versionIdx] || {}).has_comments &&
              (mayViewAllComments || (isLatest && mayViewComments))
            );
          })
          .map((f) => f.link_id)
      ),
    };
  }, [phases, selectedVersion, mayViewComments, mayViewAllComments]);

  const toggleOptionSelected = (opt, id, checked) => {
    setSelected(
      update(selected, {
        [opt]: (arr) => (checked ? arr.concat([id]) : arr.filter((a) => a != id)),
      })
    );
  };

  const toggleAllSelected = (opt, checked) => {
    setSelected(
      update(selected, {
        [opt]: {
          $set: checked ? selectableFormIds[opt] : [],
        },
      })
    );
  };

  const togglePhaseFormsSelected = (phaseId, checked) => {
    const phase = phases.find((p) => p.id === phaseId);
    if (!phase) return;
    const phaseFormIds = phase.forms.map((f) => f.link_id);
    setSelected((prev) =>
      update(prev, {
        form: (arr) =>
          checked
            ? [...new Set([...arr, ...phaseFormIds])]
            : arr.filter((id) => !phaseFormIds.includes(id)),
      })
    );
  };

  const togglePhaseExpanded = (id) => {
    setExpandedPhases((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const additionalItems = [
    'project_identifier',
    'project_title',
    'table_of_contents'
  ];

  const allFormsSelected =
    selectableFormIds.form.length > 0 &&
    selectableFormIds.form.every((id) => selected.form.includes(id));
  const someFormsSelected = selectableFormIds.form.some((id) =>
    selected.form.includes(id)
  );

  const ContentSelection = (
    <form ref={formRef}>
      <div className="pdf-export-layout">
        {/* Left panel – phase / form accordion */}
        <div className="pdf-export-left-panel">
          <div className="pdf-export-select-all-row">
            <label>
              <input
                type="checkbox"
                checked={allFormsSelected}
                ref={(el) => { if (el) el.indeterminate = !allFormsSelected && someFormsSelected; }}
                onChange={(e) => toggleAllSelected("form", e.target.checked)}
              />
              <strong>{I18n.t("select_all_option_form")}</strong>
            </label>
          </div>
          {phases.map((phase) => {
            const phaseFormIds = phase.forms.map((f) => f.link_id);
            const allPhaseSelected =
              phaseFormIds.length > 0 &&
              phaseFormIds.every((id) => selected.form.includes(id));
            const somePhaseSelected = phaseFormIds.some((id) =>
              selected.form.includes(id)
            );
            const isExpanded = expandedPhases[phase.id] !== false;

            return (
              <div key={phase.id} className="pdf-export-phase-block">
                <div className={`pdf-export-phase-header${isExpanded ? ' expanded' : ''}`}>
                  <input
                    type="checkbox"
                    checked={allPhaseSelected}
                    ref={(el) => { if (el) el.indeterminate = !allPhaseSelected && somePhaseSelected; }}
                    onChange={(e) => togglePhaseFormsSelected(phase.id, e.target.checked)}
                  />
                  <span
                    className="pdf-export-phase-name"
                    onClick={() => togglePhaseExpanded(phase.id)}
                  >
                    {phase.name}
                  </span>
                  <span
                    className="pdf-export-phase-toggle"
                    onClick={() => togglePhaseExpanded(phase.id)}
                  >
                    <FaIcon type={isExpanded ? 'chevron-down' : 'chevron-right'} />
                  </span>
                </div>
                {isExpanded && (
                  <div className="pdf-export-phase-body">
                    {maySelectVersion && !isBulkAction ? (
                      <div className="pdf-export-version-select">
                        <Select
                          name={`phases[${phase.id}][answer_id]`}
                          options={phase.version_options}
                          value={selectedVersion[phase.id]}
                          onChange={(v) => updatePhaseVersion(phase.id, v)}
                          menuPortalTarget={document.body}
                          classNamePrefix="pdf-version-select"
                          selectComponents={{ DropdownIndicator: CaretDropdownIndicator }}
                          styles={{
                            container: (provided) => ({
                              ...provided,
                              width: "60%",
                              boxShadow: "none",
                              border: "none"
                            }),
                          }}
                        />
                      </div>
                    ) : (
                      <input
                        type="hidden"
                        name={`phases[${phase.id}][answer_id]`}
                        value={selectedVersion[phase.id] || ''}
                      />
                    )}
                    {phase.forms.map((form) => {
                      const versionIdx = phase.version_options.findIndex(
                        (o) => o.value == selectedVersion[phase.id]
                      );
                      const isLatest = versionIdx === 0;
                      const hasComments = (form.answers[versionIdx] || {}).has_comments;
                      const canViewComments =
                        mayViewComments &&
                        hasComments &&
                        (mayViewAllComments || isLatest);
                      const isFormSelected = selected.form.includes(form.link_id);
                      const isCommentSelected = selected.comments.includes(form.link_id);
                      return (
                        <div key={form.link_id} className="pdf-export-form-row">
                          <input
                            type="checkbox"
                            id={`form_${form.link_id}`}
                            name={`phases[${phase.id}][forms][${form.link_id}][options][]`}
                            value="form"
                            checked={isFormSelected}
                            onChange={() => toggleOptionSelected("form", form.link_id, !isFormSelected)}
                          />
                          <label
                            htmlFor={`form_${form.link_id}`}
                            className="pdf-export-form-label"
                          >
                            {form.name[I18n.locale]}
                          </label>
                          {canViewComments && (
                            <>
                              <input
                                type="checkbox"
                                id={`comments_${form.link_id}`}
                                name={`phases[${phase.id}][forms][${form.link_id}][options][]`}
                                value="comments"
                                checked={isCommentSelected}
                                onChange={() => toggleOptionSelected("comments", form.link_id, !isCommentSelected)}
                              />
                              <label
                                htmlFor={`comments_${form.link_id}`}
                                className="pdf-export-comments-label"
                              >
                                {I18n.t("comments")}
                              </label>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Right panel – additional items + history */}
        <div className="pdf-export-right-panel">
          <div className="pdf-export-right-section">
            <strong>{I18n.t("additional_items")}</strong>
            <div className="pdf-export-right-section-items">
              {additionalItems.map((key) => (
                <div key={key} className="pdf-export-item-row">
                  <input
                    id={`additional_items[${key}]`}
                    type="checkbox"
                    name={`additional_items[${key}]`}
                    value="true"
                    onChange={onConfigUpdated}
                  />
                  <label htmlFor={`additional_items[${key}]`}>
                    {I18n.t(key === 'project_title' ? 'project_title_long' : key)}
                  </label>
                </div>
              ))}
              <div className="pdf-export-item-row">
                <input
                  id="additional_items[disclaimer_text]"
                  type="checkbox"
                  name="additional_items[disclaimer_text]"
                  value="true"
                  onChange={onDisclaimerCheck}
                />
                <label htmlFor="additional_items[disclaimer_text]">
                  {I18n.t("disclaimer_text")}
                </label>
              </div>
              {disClaimerChecked && (
                <div className="pdf-export-disclaimer-content">
                  <label>{I18n.t("disclaimer_content")}</label>
                  <RichText
                    id="additional_items[disclaimer_content]"
                    fieldname="additional_items[disclaimer_content]"
                    updateAnswer={onConfigUpdated}
                    simpleInput={true}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="pdf-export-right-section">
            <strong>{I18n.t("history")}</strong>
            {historyOption === 'full' && (
              <input type="hidden" name="additional_items[full_history]" value="true" />
            )}
            {historyOption === 'condensed' && (
              <input type="hidden" name="additional_items[condensed_history]" value="true" />
            )}
            <div className="pdf-export-right-section-items">
              {['none', 'full', 'condensed'].map((option) => (
                <div key={option} className="pdf-export-item-row">
                  <input
                    id={`history_${option}`}
                    type="radio"
                    name="history_option"
                    value={option}
                    checked={historyOption === option}
                    onChange={() => { setHistoryOption(option); onConfigUpdated(); }}
                  />
                  <label htmlFor={`history_${option}`}>
                    {I18n.t(option === 'none' ? 'none' : option)}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </form>
  );

  if (loading) return <LoadingIndicator />;

  if (phases.length == 0) return <b>{I18n.t("no_forms")}</b>;

  return ContentSelection;
};

export const ProjectReportExportFillPhaseFormsAsPdf = ({portalProps, btnProps, ...rest}) => {
  const { projectReportId } = portalProps;
  const previewPath = project_report_export_fill_phase_forms_as_pdf_path({ project_report_id: projectReportId });

  return <PdfButton
    btnProps={btnProps}
    portalProps={{
      defaultParams: {design: DEFAULT_DESIGN},
      contentTabProps: {...portalProps},
      ContentTab: false,
      enableDesign: false,
      previewPath,
      action: previewPath
    }}
  />
};

const ProjectReportPdfExport = ({portalProps, btnProps, ...rest}) => {
  if (portalProps.exportFillPhaseForms) {
    return <ProjectReportExportFillPhaseFormsAsPdf {...{portalProps, btnProps, ...rest}}/>
  }

  const { projectReportId } = portalProps;
  const isBulkAction = Array.isArray(projectReportId);
  const pdfAction = isBulkAction
    ? project_report_external_bulk_pdf_path({ project_report_ids: projectReportId })
    : project_report_pdf_path({ project_report_id: projectReportId });
  const previewPath = isBulkAction
    ? project_report_pdf_path({ project_report_id: projectReportId.length > 0 ? projectReportId[0] : 0 })
    : project_report_pdf_path({ project_report_id: projectReportId });

  return <PdfButton
    btnProps={btnProps}
    portalProps={{
      action: pdfAction,
      contentTabProps: {...portalProps},
      ContentTab: ReportPdfContentConfiguration,
      isBulkAction,
      previewPath
    }}
  />
}

export default ProjectReportPdfExport;